import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { useUIStore } from '@/store';
import { cn } from '@core/utils';
import { useMobileViewport } from '@/hooks/use-mobile-viewport';
import { useRoleBasedMenu } from '@/hooks/use-role-based-menu';
import { menuContainsPath, type MenuItem } from '@/config/menu';
import { getSidebarMenuIcon } from './menu-icon-map';
import './sidebar.css';

/** Count pill for a menu entry; `rail` pins it to the icon in the collapsed sidebar. */
function MenuBadge({ count, rail = false }: { count?: number; rail?: boolean }) {
  if (!count) return null;
  return (
    <span
      className={cn('sidebar__badge', rail && 'sidebar__badge--rail')}
      aria-label={`${count} need${count === 1 ? 's' : ''} action`}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

interface MenuItemProps {
  item: MenuItem;
  isCollapsed: boolean;
  level?: number;
}

function MenuItemComponent({ item, isCollapsed, level = 0 }: MenuItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  // Collapsed rail only: children pop out beside the icon instead of being
  // unreachable. Anchored to the button's viewport rect and rendered `fixed`,
  // so the narrow sidebar does not clip it.
  const [flyoutAt, setFlyoutAt] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const hasChildren = item.children && item.children.length > 0;
  const safeLabel = typeof item.label === 'string' ? item.label : 'Menu Item';
  const { pathname } = useLocation();
  const containsActivePath = Boolean(item.children && menuContainsPath(item.children, pathname));

  // Open the group that holds the current page, so the active entry is visible.
  // Never closes one: a group the user opened stays open while they navigate.
  useEffect(() => {
    if (containsActivePath) setIsExpanded(true);
  }, [containsActivePath]);

  // Expanding the sidebar makes the flyout redundant — the accordion takes over.
  useEffect(() => {
    if (!isCollapsed) setFlyoutAt(null);
  }, [isCollapsed]);

  // Dismiss on outside click, Escape, or scroll (the anchor point would drift).
  useEffect(() => {
    if (!flyoutAt) return;
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (flyoutRef.current?.contains(target)) return;
      if (buttonRef.current?.contains(target)) return;
      setFlyoutAt(null);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFlyoutAt(null);
    };
    // Named, so add and remove share one reference — an inline arrow here would
    // register a new listener on every open and never detach the old one.
    const onScroll = () => setFlyoutAt(null);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [flyoutAt]);

  /** Collapsed: pop the submenu out. Expanded: toggle the inline accordion. */
  const handleParentClick = () => {
    if (!isCollapsed) {
      setIsExpanded((previous) => !previous);
      return;
    }
    if (flyoutAt) {
      setFlyoutAt(null);
      return;
    }
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) setFlyoutAt({ top: rect.top, left: rect.right + 8 });
  };

  // External URL (opens in new tab)
  if (item.externalUrl) {
    return (
      <a
        href={item.externalUrl}
        target="_blank"
        rel="noopener noreferrer"
        title={!isCollapsed ? safeLabel : undefined}
        className="sidebar__menu-item"
        style={{
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          paddingLeft: isCollapsed ? '12px' : `${12 + level * 16}px`,
        }}
      >
        <span className="sidebar__menu-item-icon">{getSidebarMenuIcon(safeLabel, item.icon, 20)}</span>
        {!isCollapsed && <span>{safeLabel}</span>}
      </a>
    );
  }

  // Parent item with children (nested menu)
  if (hasChildren) {
    return (
      <div>
        <button
          ref={buttonRef}
          onClick={handleParentClick}
          title={safeLabel}
          aria-expanded={isCollapsed ? Boolean(flyoutAt) : isExpanded}
          aria-haspopup={isCollapsed ? 'menu' : undefined}
          className={cn(
            'sidebar__menu-item sidebar__menu-item--parent',
            (isExpanded || flyoutAt) && 'sidebar__menu-item--active'
          )}
          style={{
            minHeight: '40px',
            justifyContent: isCollapsed ? 'center' : 'space-between',
            paddingLeft: isCollapsed ? '12px' : `${12 + level * 16}px`,
            border: 'none',
            cursor: 'pointer',
            width: '100%',
            textAlign: 'left',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span
              style={{
                fontSize: '18px',
                minWidth: '24px',
                display: 'flex',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              {getSidebarMenuIcon(safeLabel, item.icon, 20)}
              {isCollapsed && <MenuBadge count={item.badge} rail />}
            </span>
            {!isCollapsed && <span>{safeLabel}</span>}
          </div>
          {/* Closed group: show the children's total; once open they show their own. */}
          {!isCollapsed && !isExpanded && <MenuBadge count={item.badge} />}
          {!isCollapsed && (
            <span
              style={{
                fontSize: '12px',
                transition: 'transform 0.2s ease',
                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
              }}
            >
              ▸
            </span>
          )}
        </button>

        {/* Inline accordion — expanded sidebar only. */}
        {isExpanded && !isCollapsed && (
          <div style={{ marginTop: '2px' }}>
            {item.children?.map((child, index) => (
              <MenuItemComponent
                key={`${typeof child.label === 'string' ? child.label : 'item'}-${index}`}
                item={child}
                isCollapsed={isCollapsed}
                level={level + 1}
              />
            ))}
          </div>
        )}

        {/* Pop-out submenu — collapsed rail only. Children render un-collapsed
            so their labels are readable, and the group name heads the panel. */}
        {isCollapsed && flyoutAt && (
          <div
            ref={flyoutRef}
            role="menu"
            aria-label={safeLabel}
            className="sidebar__flyout"
            style={{ top: flyoutAt.top, left: flyoutAt.left }}
          >
            <div className="sidebar__flyout-title">{safeLabel}</div>
            {/* Close on a link, not on a nested group's toggle (Builder AI under
                My Team), which has to stay open to show its own children. */}
            <div
              onClick={(event) => {
                if ((event.target as HTMLElement).closest('a')) setFlyoutAt(null);
              }}
            >
              {item.children?.map((child, index) => (
                <MenuItemComponent
                  key={`${typeof child.label === 'string' ? child.label : 'item'}-${index}`}
                  item={child}
                  isCollapsed={false}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Regular link item
  if (item.path) {
    return (
      <NavLink
        to={item.path}
        title={!isCollapsed ? safeLabel : undefined}
        className={({ isActive }) =>
          cn(
            'sidebar__menu-item no-underline',
            isActive && 'sidebar__menu-item--active'
          )
        }
        style={{
          minHeight: '40px',
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          paddingLeft: isCollapsed ? '12px' : `${12 + level * 16}px`,
        }}
      >
        <span
          style={{
            fontSize: '18px',
            minWidth: '24px',
            display: 'flex',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          {getSidebarMenuIcon(safeLabel, item.icon, 20)}
          {isCollapsed && <MenuBadge count={item.badge} rail />}
        </span>
        {!isCollapsed && <span>{safeLabel}</span>}
        {!isCollapsed && <MenuBadge count={item.badge} />}
      </NavLink>
    );
  }

  return null;
}

/**
 * Desktop and tablet: a column beside the content, expanded or collapsed to a rail by the
 * persisted `sidebarOpen` preference.
 *
 * Phones: an off-canvas drawer over the content, opened from the header's menu button or the
 * tab bar's Menu tab. A rail would cost every phone screen 60px it cannot spare, and the
 * expanded column left 100px of a 360px screen. The drawer always renders expanded and
 * closes on navigation, the backdrop, or Escape; the desktop preference is left untouched.
 */
export function Sidebar() {
  const sidebarOpen = useUIStore((state) => state.sidebarOpen);
  const toggleSidebar = useUIStore((state) => state.toggleSidebar);
  const mobileNavOpen = useUIStore((state) => state.mobileNavOpen);
  const setMobileNavOpen = useUIStore((state) => state.setMobileNavOpen);
  const isPhone = useMobileViewport();
  const expanded = isPhone || sidebarOpen;
  const drawerOpen = isPhone && mobileNavOpen;
  const { pathname } = useLocation();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const asideRef = useRef<HTMLElement>(null);

  const menuItems = useRoleBasedMenu();

  // Any navigation — a link, the back button — shows the page it opened.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname, setMobileNavOpen]);

  // Leaving phone width with the drawer open must not leave it open for the next phone visit.
  useEffect(() => {
    if (!isPhone) setMobileNavOpen(false);
  }, [isPhone, setMobileNavOpen]);

  // A closed drawer is off screen; `inert` also takes its links out of the tab order and the
  // accessibility tree. (Not `visibility`: Chrome holds that hidden for a frame or two after
  // opening, which swallowed the focus move below.) Declared first so it runs first.
  useEffect(() => {
    if (asideRef.current) asideRef.current.inert = isPhone && !drawerOpen;
  }, [isPhone, drawerOpen]);

  // Open: focus moves into the drawer and Escape closes it. Close: focus returns to the opener.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const opener = document.activeElement as HTMLElement | null;
    closeButtonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileNavOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      opener?.focus?.();
    };
  }, [drawerOpen, setMobileNavOpen]);

  return (
    <>
      {drawerOpen && (
        <div className="sidebar__backdrop" aria-hidden="true" onClick={() => setMobileNavOpen(false)} />
      )}
      <aside
        ref={asideRef}
        id="app-sidebar"
        aria-label="Main menu"
        className={cn(
          'sidebar',
          expanded ? 'sidebar--expanded' : 'sidebar--collapsed',
          isPhone && 'sidebar--drawer',
          drawerOpen && 'sidebar--drawer-open'
        )}
      >
        {/* Top bar with Menu title and collapse button (close button on a phone) */}
        <div className="sidebar__header">
          {expanded && <div className="sidebar__title">Menu</div>}
          {isPhone ? (
            <button
              ref={closeButtonRef}
              onClick={() => setMobileNavOpen(false)}
              className="sidebar__toggle-button sidebar__close-button"
              aria-label="Close menu"
            >
              <X size={20} />
            </button>
          ) : (
            <button
              onClick={toggleSidebar}
              className="sidebar__toggle-button"
              style={{
                marginLeft: sidebarOpen ? '0' : 'auto',
                marginRight: sidebarOpen ? '0' : 'auto',
              }}
              title={sidebarOpen ? 'Collapse' : 'Expand'}
              aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            >
              {sidebarOpen ? '«' : '»'}
            </button>
          )}
        </div>

        {/* Navigation items */}
        {/* A tapped link has done its job, even one to the page already showing (no pathname
            change for the effect above to see). A group toggle is a button and stays open. */}
        <nav
          className="sidebar__nav"
          onClick={(event) => {
            if (drawerOpen && (event.target as HTMLElement).closest('a')) setMobileNavOpen(false);
          }}
        >
          {menuItems.map((item, index) => (
            <MenuItemComponent
              key={`${typeof item.label === 'string' ? item.label : 'item'}-${index}`}
              item={item}
              isCollapsed={!expanded}
            />
          ))}
        </nav>
      </aside>
    </>
  );
}
